/**
 * Co2Chart.jsx : courbe du CO2 d'une session importée depuis un capteur
 * externe (D253, lot 2).
 *
 * Une seule série, construite sur les seules lectures porteuses d'un CO2 ;
 * ligne en tirets à la valeur max recommandée (CO2_THRESHOLDS.referenceMax),
 * toujours visible : le haut de l'axe la dépasse d'une marge. Couleurs par
 * tokens du thème seulement. Bornes d'axe calculées sur les valeurs mesurées,
 * jamais Math.min/max sur des null ([envchart-minmax-null]). Rendue seulement
 * quand la session a du CO2 (SessionCharts) ; zoom et « Analyser la vue
 * zoomée » comme les autres graphiques de la page.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { BTN, getTheme } from '../constants/theme';
import { CO2_THRESHOLDS } from '../constants/pmThresholds';
import { formatDateLocale, formatNumberLocale } from '../utils/formatters';
import { isFiniteNumber } from '../utils/statsUtils';
import useChartZoomWindow from './useChartZoomWindow';
import AnalyzeZoomButton from './AnalyzeZoomButton';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, annotationPlugin, zoomPlugin);

// Marge de l'axe au-dessus du plus haut point (ou de la référence) et sous le plus bas.
const AXIS_MARGIN = 0.1;

/** Lectures porteuses d'un CO2 mesuré, dans l'ordre reçu (un vrai 0 est gardé). */
export function co2Readings(readings) {
  return (readings || []).filter(r => isFiniteNumber(r?.co2));
}

/**
 * Bornes suggérées de l'axe en ppm : le haut dépasse toujours la ligne de
 * référence d'une marge, le bas s'approche de la plus petite valeur sans
 * descendre sous zéro. Boucle simple : jusqu'à 100 000 mesures (D235).
 */
export function co2AxisBounds(values, referenceMax) {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min)) { min = referenceMax; max = referenceMax; }
  return {
    suggestedMin: Math.max(0, Math.floor(min * (1 - AXIS_MARGIN))),
    suggestedMax: Math.ceil(Math.max(max, referenceMax) * (1 + AXIS_MARGIN)),
  };
}

const Co2Chart = ({ readings, darkMode = false, onAnalyzeZoom }) => {
  const { t } = useTranslation();
  const theme = getTheme(darkMode);
  const measured = useMemo(() => co2Readings(readings), [readings]);
  const zoom = useChartZoomWindow({ readings: measured, onAnalyzeZoom, panModifierKey: 'shift', wheelSpeed: 0.1, limitToData: true });

  if (measured.length === 0) return null;

  const referenceMax = CO2_THRESHOLDS.referenceMax;
  const values = measured.map(r => r.co2);
  const bounds = co2AxisBounds(values, referenceMax);
  const ppm = (value) => formatNumberLocale(value, 0);

  const data = {
    // Label d'axe X (affichage) : heure « HH:MM » localisée (FR/EN identiques, colon).
    labels: measured.map(r => formatDateLocale(r.timestamp, { hour: '2-digit', minute: '2-digit' })),
    datasets: [{
      label: t('charts.series.co2'),
      data: values,
      borderColor: theme.info,
      backgroundColor: theme.info,
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      fill: false,
      tension: 0.3,
    }],
  };

  const grid = { color: theme.borderLight, drawBorder: false };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { color: theme.textPrimary, font: { size: 12, weight: '600' }, usePointStyle: true, padding: 15 },
      },
      tooltip: {
        backgroundColor: theme.surfaceElevated,
        titleColor: theme.textPrimary,
        bodyColor: theme.textSecondary,
        borderColor: theme.border,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${ppm(context.parsed.y)} ppm`,
          footer: (items) => {
            const timestamp = measured[items[0].dataIndex]?.timestamp;
            return timestamp
              ? t('charts.tooltip.time', { time: formatDateLocale(timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) })
              : '';
          },
        },
      },
      annotation: {
        annotations: {
          referenceMax: {
            type: 'line',
            yMin: referenceMax,
            yMax: referenceMax,
            borderColor: theme.warning,
            borderWidth: 1.5,
            borderDash: [6, 4],
            label: {
              display: true,
              content: t('charts.co2.referenceLine', { v: ppm(referenceMax) }),
              position: 'start',
              xAdjust: 6,
              yAdjust: 12,
              backgroundColor: theme.surface,
              color: theme.textPrimary,
              borderColor: theme.warning,
              borderWidth: 1,
              font: { size: 10, weight: '600' },
              padding: 4,
              borderRadius: 3,
            },
          },
        },
      },
      zoom: zoom.zoomOptions,
    },
    scales: {
      x: { grid, ticks: { color: theme.textSecondary, font: { size: 11 }, maxTicksLimit: 20 } },
      y: {
        type: 'linear',
        position: 'left',
        suggestedMin: bounds.suggestedMin,
        suggestedMax: bounds.suggestedMax,
        grid,
        ticks: { color: theme.textSecondary, font: { size: 11 } },
        title: { display: true, text: t('charts.axis.co2'), color: theme.textSecondary, font: { size: 12, weight: '600' } },
      },
    },
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '8px' }}>
        <AnalyzeZoomButton zoom={zoom} />
        <button type="button" onClick={zoom.resetZoom}
          style={{ ...BTN.primary, padding: '6px 12px', fontSize: '12px', borderRadius: '6px' }}>
          {t('common.zoom.reset')}
        </button>
      </div>
      <div style={{ position: 'relative', height: '300px' }}>
        <Line ref={zoom.chartRef} data={data} options={options} role="img" aria-label={t('charts.aria.co2')} />
      </div>
    </div>
  );
};

export default React.memo(Co2Chart, (prev, next) =>
  prev.readings === next.readings &&
  prev.darkMode === next.darkMode &&
  prev.onAnalyzeZoom === next.onAnalyzeZoom
);
